<template>
  <div
    :id="getIdentifierAttribute"
    class="product-listing"
  >
    <fn-section
      constrained="l"
      padding-top="xs"
      padding-bottom="xs"
    >
      <fn-grid>
        <fn-grid-item
          v-if="!hideSidebar && !emptyProductSearch"
          class="show-m"
          columns="12"
          columns-m="3"
        >
          <fn-sidebar-navigation
            v-if="nodes?.nodes"
            :nodes="nodes.nodes"
            :hide-nav-links-from-crawl="hideNavLinksFromCrawl"
          />

          <fn-adobe-target-mbox id="plp_promoSlot_1" />

          <fn-section>
            <fn-cms-section-loader
              v-if="page?.content?.content_slots?.b?.sections"
              :sections="page.content.content_slots.b.sections"
            />
          </fn-section>
        </fn-grid-item>
        <fn-grid-item
          columns="12"
          :columns-m="hideSidebar ? 12 : 9"
        >
          <fn-breadcrumbs
            v-if="!emptyProductSearch"
            :nodes="breadcrumbs"
            class="show-m"
          />
          <fn-link
            v-if="activeNode && activeNode.parent && !isSearchPage"
            :to="activeNode.parent.url"
            pre-arrow
            arrow-left
            class="hide-m"
          >
            {{ activeNode.parent.title }}
          </fn-link>

          <fn-headline
            v-if="!isSearchPage"
            type="h1"
            size="h2"
          >
            {{ productListHeadline }}
          </fn-headline>
          <div v-if="hasProducts">
            <div class="pl-head">
              <fn-paragraph v-if="isSearchPage && correctedSearchTerm && !isSmartQueryEnabled">
                <span v-html="$t('productListing.correctedSearchTerm', { term: correctedSearchTerm })" />
              </fn-paragraph>

              <fn-slider-navigation
                v-show="activeNode && activeNode.nodes"
                class="hide-m"
                :nodes="activeNode && activeNode.nodes ? activeNode.nodes : []"
                :hide-from-crawl="hideNavLinksFromCrawl"
              />
            </div>

            <!-- Component slot is hidden after filter-->
            <template v-if="!componentSlotAHidden">
              <div :class="['pl-component-wrapper', { 'plcw-has-teaser': hasTeaserImage }]">
                <fn-cms-component-loader
                  v-for="component in page.content.component_slots.a.components"
                  :key="component.type"
                  :data="component.data"
                  :type="component.type"
                />
              </div>
            </template>

            <fn-facets
              v-show="!isMobileView"
              class="show-m"
              desktop
              :facets="productList?.facets"
              :hidden-filters="page?.content?.config?.filters"
              :is-brand-page="page?.isBrandPage"
              @filter="handleFilter"
              @delete-all-facets="$emit('deleteAllFacets')"
              @show-results="closeOffCanvasMenu"
            />

            <client-only>
              <teleport
                v-if="isMobileView"
                to="#off-canvas-facets"
              >
                <fn-facets
                  :facets="productList?.facets"
                  :total-results="paginationProductCount"
                  :hidden-filters="page?.content?.config?.filters"
                  :is-brand-page="page?.isBrandPage"
                  lazy
                  @filter="handleFilter"
                  @delete-all-facets="$emit('deleteAllFacets')"
                  @show-results="closeOffCanvasMenu"
                />
              </teleport>
            </client-only>

            <div class="pl-actions">
              <div
                v-if="productList?.sorts"
                class="pl-action sorting-options"
              >
                <fn-choices
                  id="product-listing-choices"
                  class="choices--sorting"
                  show-sorting-header
                  :options="mappedProductListSortItems"
                  :value="activeSortItem"
                  @update:value="handleSorting"
                />
              </div>

              <div
                v-if="!isAppview"
                class="pl-action filter"
              >
                <fn-button
                  secondary
                  icon="filter"
                  @click="toggleOffCanvasFacets"
                >
                  {{ $t('filter.filters') }}
                </fn-button>
              </div>

              <div
                v-if="selectedFilterItems.length"
                class="facet-item-filter pl-action"
              >
                <fn-facet-item-filter :facet-items="selectedFilterItems" />
              </div>

              <fn-divider>
                <i18n-t
                  keypath="pagination.productCount"
                  :plural="paginationProductCount"
                >
                  <template #productCount>
                    {{ paginationProductCount }}
                  </template>
                </i18n-t>
                {{ ' ' }}
                <span v-if="isSearchPage && searchTermDivider">
                  {{ $t('productListing.forSearchTerm') }}
                  „{{ searchTermDivider }}“
                </span>
              </fn-divider>
            </div>

            <fn-grid
              class="product-grid"
              subgrid
            >
              <template v-for="(item, index) in listToDisplay">
                <fn-grid-item
                  v-if="itemIsProduct(item)"
                  :key="item.code"
                  class="grid-item--product"
                  columns="6"
                  columns-m="3"
                  v-bind="getItemOrder(index, item)"
                  static
                >
                  <fn-product-teaser
                    :product="getProductTeaserProduct(item)"
                    :is-search-page="isSearchPage"
                    @add-to-cart="handleAddToCart"
                  />
                </fn-grid-item>
                <fn-grid-item
                  v-else
                  :key="`${index}-${item.size}`"
                  class="grid-item--content-tile"
                  :columns="item.isDoubleTile ? 12 : 6"
                  :columns-m="item.isDoubleTile ? 6 : 3"
                  :style="{ gridColumn: item.isDoubleTile ? 'span 2' : null }"
                  v-bind="getItemOrder(index, item)"
                  static
                >
                  <client-only>
                    <fn-content-tile-double
                      v-if="item.isDoubleTile"
                      :content-tile="item"
                    />
                    <fn-content-tile
                      v-else
                      :content-tile="item"
                    />
                  </client-only>
                </fn-grid-item>
              </template>
            </fn-grid>
          </div>
          <div v-if="hasProductsLoaded && !hasProducts">
            <fn-section>
              <fn-image-teaser v-if="!isSearchPage">
                <template #default>
                  <div class="img-empty">
                    <div class="img-empty-top-container">
                      <fn-image
                        class="img-empty--top show-m"
                        :src="noProdImgDesktopTop.src"
                        :width="noProdImgDesktopTop.width"
                        :height="noProdImgDesktopTop.height"
                        role="presentation"
                      />
                      <fn-image
                        class="img-empty--top hide-m"
                        :src="noProdImgMobileTop.src"
                        :width="noProdImgMobileTop.width"
                        :height="noProdImgMobileTop.height"
                        role="presentation"
                      />

                      <div class="empty-teaser-content">
                        <span class="etc-title">
                          {{ $t('productListing.empty.title') }}
                        </span>
                        <span class="etc-content">
                          {{ emptyProductListingDescription }}
                        </span>
                        <div
                          v-if="hasSearchedWithFilters"
                          class="etc-button"
                        >
                          <fn-button
                            class="show-m"
                            secondary-alt
                            @click="deleteAllFacets(productList?.facets)"
                          >
                            {{ $t('filter.deleteAllFilters') }}
                          </fn-button>
                          <a
                            class="hide-m"
                            @click="deleteAllFacets(productList?.facets)"
                          >
                            {{ $t('filter.deleteAllFilters') }}
                          </a>
                        </div>
                      </div>
                    </div>

                    <fn-image
                      class="show-m"
                      :src="noProdImgDesktop.src"
                      :width="noProdImgDesktop.width"
                      :height="noProdImgDesktop.height"
                      :alt="$t('productListing.empty.title')"
                    />
                    <fn-image
                      class="hide-m"
                      :src="noProdImgMobile.src"
                      :width="noProdImgMobile.width"
                      :height="noProdImgMobile.height"
                      :alt="$t('productListing.empty.title')"
                    />
                  </div>
                </template>
              </fn-image-teaser>

              <fn-panel-image
                v-else
                column-size="6"
              >
                <template #image>
                  <fn-image
                    :src="noResultsImage.src"
                    :width="noResultsImage.width"
                    :height="noResultsImage.height"
                    class="show-m"
                    role="presentation"
                  />
                </template>
                <template #default>
                  <div class="no-search-results">
                    <div class="nsr-figure">
                      <fn-image
                        :src="noResultsIcon.src"
                        :width="noResultsIcon.width"
                        :height="noResultsIcon.height"
                        role="presentation"
                      />
                    </div>
                    <div class="nsr-content">
                      <p v-html="emptySearchDescription" />

                      <template v-if="hasSearchedWithFilters">
                        <fn-button
                          class="show-m"
                          secondary-alt
                          @click="deleteAllFacets(productList?.facets)"
                        >
                          {{ $t('filter.deleteAllFilters') }}
                        </fn-button>
                        <a
                          class="hide-m"
                          @click="deleteAllFacets(productList?.facets)"
                        >
                          {{ $t('filter.deleteAllFilters') }}
                        </a>
                      </template>
                      <template v-else>
                        <fn-headline
                          type="h5"
                          size="h2"
                        >
                          {{ $t('search.empty.tip.headline') }}
                        </fn-headline>

                        <fn-list
                          unstyled
                          icons
                          spaced
                        >
                          <li
                            v-for="(bullet, index) in $tm('search.empty.tip.bullets')"
                            :key="index"
                          >
                            <fn-icon
                              icon="check-bold"
                              color="green-dark"
                              size="xs"
                            />
                            <span v-html="$rt(bullet)" />
                          </li>
                        </fn-list>
                      </template>
                    </div>
                  </div>
                </template>
              </fn-panel-image>
            </fn-section>
            <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
              <fn-section
                v-show="visible"
                background-color="white"
                constrained="m"
                padding-bottom="s"
              >
                <fn-adobe-target-mbox id="recoSlot1" />
              </fn-section>
            </fn-adobe-target-mbox-wrapper>
            <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
              <fn-section
                v-show="visible"
                background-color="white"
                constrained="m"
                padding-bottom="s"
              >
                <fn-adobe-target-mbox id="recoSlot2" />
              </fn-section>
            </fn-adobe-target-mbox-wrapper>
            <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
              <fn-section
                v-show="visible"
                background-color="white"
                constrained="m"
                padding-bottom="s"
              >
                <fn-adobe-target-mbox id="recoSlot3" />
              </fn-section>
            </fn-adobe-target-mbox-wrapper>
            <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
              <fn-section
                v-show="visible"
                background-color="white"
                constrained="m"
                padding-bottom="s"
              >
                <fn-adobe-target-mbox id="recoSlot4" />
              </fn-section>
            </fn-adobe-target-mbox-wrapper>
          </div>

          <fn-pagination
            v-if="hasProducts && productList?.pagination?.totalPages > 1"
            :current-page="productList.pagination.currentPage"
            :total-pages="productList.pagination.totalPages"
            :product-listing-page-activated="filterPageActivated"
            @go-to-page="handleGoToPage"
          />
        </fn-grid-item>
      </fn-grid>
    </fn-section>

    <fn-section v-if="isFirstPage">
      <fn-cms-section-loader
        v-if="page?.content?.content_slots?.a?.sections"
        :sections="page.content.content_slots.a.sections"
      />
    </fn-section>

    <fn-seo-related-links
      v-if="seoRelatedLinks.length"
      :related-links="seoRelatedLinks"
    />
  </div>
</template>

<script lang="ts">
import { getProductListingContentTiles, getProductSearchContentTiles } from '@/utils/content-tiles';

// Import images

/* eslint-disable import/no-duplicates */
import NoResultsImage from '@/assets/img/error-404.png';
import NoResultsImageSize from '@/assets/img/error-404.png?imageSize';
import NoResultsIcon from '@/assets/img/lupe_404.png';
import NoResultsIconSize from '@/assets/img/lupe_404.png?imageSize';
import NoProdImgDesktop from '@/assets/img/no-products-desktop.png';
import NoProdImgDesktopSize from '@/assets/img/no-products-desktop.png?imageSize';
import NoProdImgDesktopTop from '@/assets/img/no-products-desktop-top.png';
import NoProdImgDesktopTopSize from '@/assets/img/no-products-desktop-top.png?imageSize';
import NoProdImgMobile from '@/assets/img/no-products-mobile.png';
import NoProdImgMobileSize from '@/assets/img/no-products-mobile.png?imageSize';
import NoProdImgMobileTop from '@/assets/img/no-products-mobile-top.png';
import NoProdImgMobileTopSize from '@/assets/img/no-products-mobile-top.png?imageSize';
/* eslint-enable */

// Define objects with src and imageSize
const noResultsImage = {
  src: NoResultsImage,
  ...NoResultsImageSize,
};
const noResultsIcon = {
  src: NoResultsIcon,
  ...NoResultsIconSize,
};
const noProdImgDesktop = {
  src: NoProdImgDesktop,
  ...NoProdImgDesktopSize,
};
const noProdImgDesktopTop = {
  src: NoProdImgDesktopTop,
  ...NoProdImgDesktopTopSize,
};
const noProdImgMobile = {
  src: NoProdImgMobile,
  ...NoProdImgMobileSize,
};
const noProdImgMobileTop = {
  src: NoProdImgMobileTop,
  ...NoProdImgMobileTopSize,
};

export default defineComponent({
  props: {
    correctedSearchTerm: {
      type: String,
      default: '',
    },
    page: {
      type: Object,
      default: () => ({}),
    },
    productListingTemplate: {
      type: Object,
      default: () => ({}),
    },
    seoText: {
      type: String,
      default: null,
    },
    relatedBrands: {
      type: Array,
      default: undefined,
    },
    productList: {
      type: Array,
      default: undefined,
    },
    nodes: {
      type: Array,
      default: undefined,
    },
    breadcrumbs: {
      type: Array,
      default: undefined,
    },
    hideSidebar: {
      type: Boolean,
    },
    filterPageActivated: {
      type: Boolean,
    },
  },
  emits: [
    // // Event emitters for ProductSearch middleware
    // 'hideNav',
    // 'hideNavFlyout',

    // Event emitters for useFacets composable
    'deleteAllFacets',
    'showResults',
  ],
  async setup(props, context) {
    const { deleteAllFacets } = useFacets(context);
    const productListFunctions = useProductListing(props.page);
    const hasProductsLoaded = computed(() => !!props.productListingTemplate?.productList);

    return {
      ...productListFunctions,
      ...props.productListingTemplate,
      hasProductsLoaded,
      deleteAllFacets,
    };
  },
  computed: {
    ...mapState(useMainStore, {
      isAppview: 'isAppview',
      isMobileView: 'isMobileView',
      features: 'getFeature',
      abTests: 'abTests',
    }),
    ...mapWritableState(useMainStore, {
      searchTerm: 'searchTerm',
      contentTiles: 'contentTiles',
      pdpSearchQuery: 'pdpSearchQuery',
    }),
    ...mapState(useUserStore, {
      tealiumAudiences: 'tealiumAudiences',
    }),
    ...mapState(useNavigationStore, {
      isOffCanvasFacetActive: 'getIsOffCanvasFacetActive',
    }),

    listToDisplay() {
      if (!this.isFirstPage || this.productList.products.length < 3 || !this.contentTilesToDisplay?.length) {
        return this.productList.products;
      }

      const mixedList = [...this.productList.products];

      this.contentTilesToDisplay.forEach((tile, currentArrIndex) => {
        const isDoubleTile = this.itemIsDoubleTile(tile);
        let tileIndex = this.getContentTileIndex(tile, isDoubleTile);

        if (tileIndex + 1 > mixedList.length) {
          return;
        }

        // This block is needed to cover all edge-cases of positions intersection
        // Skip this block for the first tile since no intersection can happen
        if (currentArrIndex) {
          const currentIndexItem = mixedList[tileIndex];

          // If null => move to the next since it is taken by double-sized tile
          if (!currentIndexItem) {
            tileIndex += 1;
          } else if (!this.itemIsProduct(mixedList[tileIndex])) {
            tileIndex += this.itemIsDoubleTile(currentIndexItem) ? 2 : 1;
          }
        }

        if (isDoubleTile) {
          // If tile is in the extreme right position then move it to the next row
          // since double-sized tile takes double space
          if (this.tileInExtremeRight(tileIndex)) {
            tileIndex += 1;
          }
          tile.inMiddle = this.tileInMiddle(tileIndex);

          mixedList.splice(tileIndex, 0, tile, null);
        } else {
          mixedList.splice(tileIndex, 0, tile);
        }

        tile.isDoubleTile = isDoubleTile;
      });

      return mixedList.filter((item) => item !== null);
    },

    componentSlotAHidden() {
      return (
        !this.page?.content?.component_slots?.a?.components ||
        (this.page?.content?.slot_config?.hide_components && this.$route.query?.q)
      );
    },

    pageCategories() {
      if (this.page?.content?.config?.filters) {
        return this.page.content.config.filters
          .filter((filter) => filter.key === 'multiCategory')
          .map((filter) => filter.value);
      }

      return [];
    },

    searchTerms() {
      return this.$route.query?.text?.toLowerCase() || this.$route.query?.q?.toLowerCase();
    },

    contentTilesToDisplay() {
      if (this.isSearchPage) {
        const list = getProductSearchContentTiles(this.contentTiles, this.tealiumAudiences, this.searchTerms);
        return this.getTilesToDisplay(list, 'search');
      }

      const list = getProductListingContentTiles(
        this.contentTiles,
        this.tealiumAudiences,
        this.$route,
        this.pageCategories,
      );
      return this.getTilesToDisplay(list, 'plps');
    },

    brandName() {
      if (!this.selectedFilterItems.length) {
        if (this.productListHeadline.length) {
          return this.productListHeadline;
        }
        return false;
      }

      const lastFacet = this.selectedFilterItems[this.selectedFilterItems.length - 1];

      if (lastFacet.parentFacet !== 'brand') {
        return false;
      }

      return lastFacet.name;
    },

    hasProducts() {
      return this.productList?.products?.length > 0;
    },

    isFirstPage() {
      return this.productList?.pagination?.currentPage === 0;
    },

    emptyProductSearch() {
      return !this.hasProducts && this.isSearchPage;
    },

    searchTermDivider() {
      let searchTerm = '';

      if (this.$route.query?.correctedText || this.$route.query?.text) {
        if (this.isSmartQueryEnabled || this.abTests.searchhub) {
          searchTerm = this.$route.query?.correctedText || this.$route.query?.text;
        } else {
          searchTerm = this.$route.query?.text;
        }
      } else if (this.$route.query.q) {
        [searchTerm] = this.$route.query.q.split(':');
        searchTerm = searchTerm?.replace('+', ' ') || '';
      } else {
        searchTerm = this.searchTerm;
      }

      return searchTerm;
    },

    selectedFilterItems() {
      let hiddenFacetCodes = null;

      if (!this.isSearchPage) {
        const filters = this.page?.content?.config?.filters || [];

        hiddenFacetCodes = filters.map((filter) => filter.key);
      }

      const selectedItems = [];

      for (const facet of this.productList?.facets) {
        if (!facet.values.length || hiddenFacetCodes?.includes(facet.code)) {
          continue;
        }

        for (const value of facet.values) {
          if (value.selected) {
            selectedItems.push({
              ...value,
              ...{ parentFacet: facet.code },
            });
          }

          // push selected subfacets if available
          const subFacets = value.values;
          if (subFacets?.length) {
            for (let k = 0; k < subFacets.length; k++) {
              if (subFacets[k].selected) {
                selectedItems.push({
                  ...subFacets[k],
                  ...{ parentFacet: facet.code },
                });
              }
            }
          }
        }
      }

      return selectedItems;
    },

    activeSortItem() {
      if (!this.productList?.sorts?.length) {
        return null;
      }

      const possibleSortItem = this.productList.sorts.find((sort) => sort.selected === true);
      if (!possibleSortItem) {
        return this.productList.sorts[0].code;
      }

      return possibleSortItem.code;
    },

    mappedProductListSortItems() {
      return this.productList?.sorts?.map((sortItem) => ({
        label: sortItem.name,
        value: sortItem.code,
        selected: sortItem.selected,
      }));
    },

    activeNode() {
      let current = null;

      this.nodes?.nodes?.forEach((node) => {
        const nodeUrl = node?.url || '';
        const pageUrl = this.page?.url || '';

        if (nodeUrl.startsWith('?') || nodeUrl.startsWith('/search') || nodeUrl.startsWith(this.page?.url)) {
          current = this.nodes;
        }

        if (pageUrl === nodeUrl) {
          node.parent = {
            title: this.nodes.title,
            url: this.nodes.url,
          };
          current = node;
        } else if (node.nodes) {
          node.nodes.forEach((child) => {
            if (child?.url === pageUrl) {
              child.parent = {
                title: node.title,
                url: node.url,
              };
              current = child;
            }
          });
        }
      });

      if (this.isSearchPage && !this.isPredefinedTextSearch) {
        if (this.nodes?.nodes?.length >= 1 && this.nodes?.nodes[0]?.nodes?.length <= 1) {
          if (this.nodes.nodes.length <= 1 && this.nodes.nodes[0].nodes.length === 0) {
            return null;
          }
          return this.nodes;
        }
        if (this.nodes?.nodes) {
          return this.nodes.nodes[0];
        }
      }

      if (current?.nodes?.length === 1 && current?.nodes[0]?.nodes?.length > 0) {
        current = current.nodes[0];
      }

      return current;
    },

    isSearchPage() {
      const hasSearchPageParams = this.productList?.freeTextSearch?.length && !this.isPredefinedTextSearch;
      const hasSearchPageUrl = this.$route.path.includes(this.$t('url.search'));

      return hasSearchPageUrl || hasSearchPageParams;
    },

    isPredefinedTextSearch() {
      return this.productList?.isPredefinedTextSearch;
    },

    noResultsImage() {
      return noResultsImage;
    },

    noResultsIcon() {
      return noResultsIcon;
    },

    noProdImgDesktopTop() {
      return noProdImgDesktopTop;
    },

    noProdImgDesktop() {
      return noProdImgDesktop;
    },

    noProdImgMobileTop() {
      return noProdImgMobileTop;
    },

    noProdImgMobile() {
      return noProdImgMobile;
    },

    sanitizedSearch() {
      return sanitizeTextInput(decodeURIComponent(this.productList?.freeTextSearch));
    },

    productListHeadline() {
      if (!this.page?.content) {
        return '';
      }

      return this.page.content.headlineOverride || this.page.content.additional_title || this.page.content.title;
    },

    getIdentifierAttribute() {
      return this.filterPageActivated ? 'active-filter-page' : '';
    },

    isSmartQueryEnabled() {
      return this.features('enableSmartQuery');
    },

    emptyProductListingDescription() {
      return this.$t(
        this.hasSearchedWithFilters ? 'productListing.empty.descriptionFilter' : 'productListing.empty.description',
      );
    },

    emptySearchDescription() {
      return this.$t(this.hasSearchedWithFilters ? 'productListing.empty.descriptionFilter' : 'search.empty.title', {
        term: this.sanitizedSearch,
      });
    },

    paginationProductCount() {
      return this.productList?.pagination?.totalResults;
    },

    seoRelatedLinks() {
      const relatedLinks = [];

      if (this.relatedBrands && this.filterPageActivated && !this.page?.isBrandPage) {
        // We need to explicitly add a new key here 'brandLink' in order
        // to properly render the content in fn-seo-related-links component.
        const relatedBrandLinks = this.relatedBrands.map((link) => ({ ...link, brandLink: true }));
        relatedLinks.push(...relatedBrandLinks);
      }

      if (this.page?.content?.related_links) {
        relatedLinks.push(...this.page.content.related_links);
      }

      return relatedLinks;
    },

    hasTeaserImage() {
      return this.page?.content?.component_slots?.a?.components?.find(
        (comp: { type: string }) => comp.type === 'image' || comp.type === 'image_teaser',
      );
    },
  },
  watch: {
    async $route() {
      this.updateSearchTerm();
    },
    // Off canvas menu only closes when the user explicitly clicks on close, but child facets might set
    // filters that return an empty product response. In this case we close it automatically, otherwise
    // the empty off canvas menu would be stuck in an un-closeable state.
    hasProducts(hasProducts) {
      if (!hasProducts) {
        this.closeOffCanvasMenu();
      }
    },
  },
  created() {
    this.updateSearchTerm();
  },
  mounted() {
    this.modifyUrlQuery();
  },
  methods: {
    ...mapActions(useNavigationStore, {
      toggleOffCanvasFacets: 'toggleOffCanvasFacets',
      closeOffCanvasMenu: 'closeOffCanvasMenu',
    }),

    updateSearchTerm() {
      this.searchTerm = this.searchTermDivider;
    },

    modifyUrlQuery() {
      if (this.isSearchPage && this.pdpSearchQuery && this.$route.query.text === this.pdpSearchQuery.text) {
        this.$router.push({ query: this.pdpSearchQuery });
      }
      this.pdpSearchQuery = '';
    },

    getProductTeaserProduct(product) {
      if (this.isSearchPage && product.url) {
        if (this.$route.query.text) {
          return {
            ...product,
            url: `${product.url}?isSearchPage=${this.$route.query.text}`,
          };
        }
        if (this.$route.query.q) {
          return {
            ...product,
            url: `${product.url}?q=${this.$route.query.q}`,
          };
        }
      }

      return product;
    },

    itemIsProduct(item) {
      return item.code;
    },

    itemIsDoubleTile(item) {
      return item.size === 'double';
    },

    getContentTileIndex(tile, tileIsDouble) {
      let tilePosition;

      if (this.isSearchPage) {
        tilePosition = tileIsDouble && tile.position_search_double ? tile.position_search_double : tile.position_search;
      } else {
        tilePosition = tileIsDouble && tile.position_plps_double ? tile.position_plps_double : tile.position_plps;
      }

      return tilePosition - 1;
    },

    getItemOrder(elemIndex, item) {
      if (this.itemIsProduct(item) || !this.itemIsDoubleTile(item) || !item.inMiddle) {
        return {
          order: elemIndex,
        };
      }

      return {
        order: elemIndex + 2,
        orderM: elemIndex,
      };
    },

    getTilesToDisplay(tiles, position) {
      const positionString = `position_${position}`;
      // Split tiles array to two arrays: personalised tiles and default tiles
      const [personalisedTiles = [], defaultTiles = []] = conditionalSplitArray(tiles, this.tileIsPersonalised);
      let sortedTiles = [];

      // If there are personalised tiles then sort by date
      if (personalisedTiles?.length) {
        sortedTiles = [...this.sortTilesByDate(personalisedTiles)];
      }

      // Put the personalised tiles on top and then other tiles
      sortedTiles = [...sortedTiles, ...this.sortTilesByDate(defaultTiles)];

      // Filter out the tiles with the same position, leave only the last updated one
      const uniqueTiles = this.getUniqueTiles(sortedTiles, positionString);

      // Display up to three tiles and sort it by position to display tiles on the right positions
      return this.sortTilesByPosition(uniqueTiles.slice(0, 3), positionString);
    },

    tileIsPersonalised(tile) {
      return tile?.add_personalization?.add_personalization?.length;
    },

    tileInExtremeRight(tileIndex) {
      return (tileIndex + 1) % 4 === 0;
    },

    tileInMiddle(tileIndex) {
      return (tileIndex + 1) % 2 === 0;
    },

    sortTilesByDate(tiles) {
      return tiles.sort((tile1, tile2) => new Date(tile2.onlineFrom) - new Date(tile1.onlineFrom));
    },

    getUniqueTiles(tiles, tilePropertyName) {
      const takenPropertyValues = [];

      return tiles.filter((tile) => {
        const tilePosition = this.correctedPropertyValue(tile, tilePropertyName);
        if (takenPropertyValues.includes(tilePosition)) {
          return false;
        }

        takenPropertyValues.push(tilePosition);
        return true;
      });
    },
    sortTilesByPosition(tiles, positionString) {
      return tiles.sort((item1, item2) => {
        const item1Position = this.correctedPropertyValue(item1, positionString);
        const item2Position = this.correctedPropertyValue(item2, positionString);

        return item1Position - item2Position;
      });
    },
    correctedPropertyValue(tile, propertyName) {
      const tileIsDouble = this.itemIsDoubleTile(tile);
      const correctedPropertyName = `${propertyName}${tileIsDouble ? '_double' : ''}`;

      return tile[correctedPropertyName];
    },
  },
});
</script>

<style lang="scss" scoped>
$noProdImgTopHeight: 130px;
$noProdImgTopCurveHeight: 40px;

.product-listing {
  position: relative;

  .img-empty {
    .img-empty-top-container {
      position: relative;
      z-index: 1;
      margin-bottom: -$noProdImgTopCurveHeight;
      padding: $pad $space ($noProdImgTopCurveHeight + $pad);

      // The noProdImgTop has a fixed height to not distort it vertically,
      // so it might not fill its parent container leaving a white bar on top.
      // Here we ensures to have the same background color on the upper part of
      // the parent container, but keep the part behind the curve transparent
      // so no one notices
      background: linear-gradient(
        to bottom,
        palette(background, green),
        /* Start with background color */ palette(background, green) calc(100% - #{$noProdImgTopCurveHeight}),
        /* Make a full-stop */ transparent calc(100% - #{$noProdImgTopCurveHeight}),
        /* Curved area */ transparent
      );
    }

    .img-empty--top {
      position: absolute;
      bottom: 0;
      left: 0;
      height: $noProdImgTopHeight;
    }
  }

  .grid-container {
    overflow: visible;
  }

  .breadcrumbs {
    margin-bottom: $pad;
  }

  .pl-head {
    margin-bottom: $pad;

    @include bp(m) {
      margin: $pad 0;
    }
  }

  .pl-component-wrapper {
    overflow: hidden;

    &.plcw-has-teaser {
      .anchor {
        height: 100%;
      }

      &:deep(.image-teaser) {
        height: 100%;
      }

      :deep(.picture) {
        height: 100%;

        @include bp(m) {
          height: 165px;
        }
      }

      :deep(img) {
        &.lazyload,
        &.lazyloading,
        &.image--loading {
          height: 100%;

          @include bp(m) {
            height: 165px;
          }
        }
      }
    }

    > * {
      margin-bottom: $space;
    }

    > .heading {
      margin-bottom: 0;
    }
  }

  .pl-actions {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: $pad;
    margin-bottom: $space;
    flex-wrap: wrap;

    .pl-action {
      &.filter {
        flex: 0 0 auto;
        margin-left: auto;

        @include bp(m) {
          display: none;
        }
      }

      &.facet-item-filter {
        align-self: flex-start;
        flex: 0 0 100%;
        margin-top: $pad;

        @include bp(m) {
          flex: 1;
          margin: 0 16px 0 0;
          order: 1;
        }
      }
    }

    .divider {
      margin-top: $space;
      flex: 100%;
      order: 4;
    }
  }

  .sorting-options {
    display: block;
    position: relative;

    @include bp(m) {
      align-items: center;
      align-self: flex-start;
      display: flex;
      margin-left: auto;
      order: 2;
    }

    label {
      margin-right: 10px;
      font-size: map-get($font-sizes, 'm');
      line-height: $ui-height;
    }

    .choices {
      max-width: 240px;
      min-width: 210px;
      margin-top: 10px;

      @include bp(m) {
        min-width: 200px;
        margin-top: 0;
      }

      :deep(span) {
        font-weight: normal;
      }
    }
  }

  .pagination {
    margin-bottom: $pad * 2;
  }
}

.empty-teaser-content {
  position: relative;
  display: grid;
  grid-gap: 0 $pad;
  grid-template-areas:
    'title button'
    'content button';
  color: $color-brand-fnmz-green;

  .etc-title {
    grid-area: title;
    font-size: map-get($font-sizes, 'l');
    font-weight: map-get($font-weights, 'bold');
  }

  .etc-content {
    grid-area: content;
    display: flex;
    flex-direction: row;
    gap: $pad;
  }

  .etc-button {
    grid-area: button;
    align-self: end;
    justify-self: end;
  }
}

.no-search-results {
  padding-bottom: 20px;

  @include bp(m) {
    padding-bottom: 0;
  }

  .nsr-figure {
    display: flex;
    justify-content: center;
    margin-bottom: $space * 2;
    max-width: 120px;
    margin-left: auto;
    margin-right: auto;
  }
}

.product-grid {
  margin-bottom: -$grid-margin-width;

  :deep(.product-teaser),
  :deep(.pt-content),
  :deep(.pricing) {
    @include subgrid-inherit();
  }

  .product-teaser {
    width: 100%;
    margin: 0;
    padding: 0 10px;
  }
}
</style>
